/**
 * Bootstrap Analytics Page with Dark Mode Support
 * Analytics dashboard using Bootstrap components and theme system
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';

// Mock analytics data
const analyticsData = {
  overview: {
    totalExecutions: 2847,
    successRate: 94.2,
    avgResponseTime: 2.4,
    activeWorkflows: 23,
    totalUsers: 15,
    dataProcessed: '2.3TB'
  },
  timeSeriesData: [
    { date: '2025-07-28', executions: 156, success: 147, errors: 9 },
    { date: '2025-07-29', executions: 203, success: 192, errors: 11 },
    { date: '2025-07-30', executions: 189, success: 178, errors: 11 },
    { date: '2025-07-31', executions: 234, success: 221, errors: 13 },
    { date: '2025-08-01', executions: 267, success: 252, errors: 15 },
    { date: '2025-08-02', executions: 298, success: 284, errors: 14 },
    { date: '2025-08-03', executions: 312, success: 296, errors: 16 }
  ],
  topWorkflows: [
    { name: 'Image Enhancement Pipeline', executions: 456, successRate: 96.3, avgTime: 2.1 },
    { name: 'Text Generation Flow', executions: 334, successRate: 89.2, avgTime: 4.8 },
    { name: 'Data Processing Chain', executions: 289, successRate: 94.8, avgTime: 3.2 },
    { name: 'API Integration Suite', executions: 267, successRate: 98.1, avgTime: 1.5 },
    { name: 'Video Processing Pipeline', executions: 198, successRate: 91.4, avgTime: 12.7 }
  ],
  errorBreakdown: [
    { type: 'Timeout Errors', count: 45, percentage: 32.1 },
    { type: 'API Rate Limits', count: 38, percentage: 27.1 },
    { type: 'Validation Errors', count: 29, percentage: 20.7 },
    { type: 'Resource Limits', count: 18, percentage: 12.9 },
    { type: 'Network Issues', count: 10, percentage: 7.1 }
  ],
  performanceMetrics: [
    { metric: 'CPU Usage', current: 68, previous: 72, trend: 'down', color: 'success' },
    { metric: 'Memory Usage', current: 82, previous: 79, trend: 'up', color: 'warning' },
    { metric: 'Disk I/O', current: 45, previous: 51, trend: 'down', color: 'success' },
    { metric: 'Network Usage', current: 34, previous: 38, trend: 'down', color: 'success' }
  ]
};

export function BootstrapAnalytics() {
  const { t } = useTranslation('platformPages');
  const { effectiveTheme } = useBootstrapTheme();
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState('executions');

  const { overview, timeSeriesData, topWorkflows, errorBreakdown, performanceMetrics } = analyticsData;

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div>
              <h1 className="h3 mb-1">{t('dashboard.title')}</h1>
              <p className="text-muted mb-0">
                {t('dashboard.subtitle')}
              </p>
            </div>
            <div className="d-flex gap-2">
              <select
                className="form-select"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                style={{ width: 'auto' }}
              >
                <option value="1d">{t('dashboard.range1d')}</option>
                <option value="7d">{t('dashboard.range7d')}</option>
                <option value="30d">{t('dashboard.range30d')}</option>
                <option value="90d">{t('dashboard.range90d')}</option>
              </select>
              <button className="btn btn-outline-primary">
                📊 {t('dashboard.exportReport')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="row g-3 mb-4">
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div className="card">
            <div className="card-body text-center">
              <div className="h2 text-primary mb-1">{overview.totalExecutions.toLocaleString()}</div>
              <div className="small text-muted">{t('dashboard.totalExecutions')}</div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div className="card">
            <div className="card-body text-center">
              <div className="h2 text-success mb-1">{overview.successRate}%</div>
              <div className="small text-muted">{t('dashboard.successRate')}</div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div className="card">
            <div className="card-body text-center">
              <div className="h2 text-info mb-1">{overview.avgResponseTime}s</div>
              <div className="small text-muted">{t('dashboard.avgResponse')}</div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div className="card">
            <div className="card-body text-center">
              <div className="h2 text-warning mb-1">{overview.activeWorkflows}</div>
              <div className="small text-muted">{t('dashboard.activeWorkflows')}</div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div className="card">
            <div className="card-body text-center">
              <div className="h2 text-primary mb-1">{overview.totalUsers}</div>
              <div className="small text-muted">{t('dashboard.totalUsers')}</div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-4 col-sm-6">
          <div className="card">
            <div className="card-body text-center">
              <div className="h2 text-secondary mb-1">{overview.dataProcessed}</div>
              <div className="small text-muted">{t('dashboard.dataProcessed')}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Main Chart Area */}
        <div className="col-lg-8">
          <div className="card mb-4">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h5 className="card-title mb-0">{t('dashboard.executionTrends')}</h5>
              <div className="btn-group btn-group-sm" role="group">
                <input
                  type="radio"
                  className="btn-check"
                  name="metric"
                  id="executions"
                  checked={selectedMetric === 'executions'}
                  onChange={() => setSelectedMetric('executions')}
                />
                <label className="btn btn-outline-primary" htmlFor="executions">{t('dashboard.executions')}</label>

                <input
                  type="radio"
                  className="btn-check"
                  name="metric"
                  id="success"
                  checked={selectedMetric === 'success'}
                  onChange={() => setSelectedMetric('success')}
                />
                <label className="btn btn-outline-success" htmlFor="success">{t('dashboard.success')}</label>

                <input
                  type="radio"
                  className="btn-check"
                  name="metric"
                  id="errors"
                  checked={selectedMetric === 'errors'}
                  onChange={() => setSelectedMetric('errors')}
                />
                <label className="btn btn-outline-danger" htmlFor="errors">{t('dashboard.errors')}</label>
              </div>
            </div>
            <div className="card-body">
              {/* Simplified chart visualization */}
              <div className="row g-2 mb-3">
                {timeSeriesData.map((data, index) => {
                  const value = selectedMetric === 'executions' ? data.executions : 
                               selectedMetric === 'success' ? data.success : data.errors;
                  const maxValue = Math.max(...timeSeriesData.map(d => 
                    selectedMetric === 'executions' ? d.executions : 
                    selectedMetric === 'success' ? d.success : d.errors
                  ));
                  const height = (value / maxValue) * 100;
                  
                  return (
                    <div key={index} className="col d-flex flex-column align-items-center">
                      <div 
                        className={`w-100 ${
                          selectedMetric === 'executions' ? 'bg-primary' :
                          selectedMetric === 'success' ? 'bg-success' : 'bg-danger'
                        } bg-opacity-75 rounded-top`}
                        style={{ height: `${Math.max(height, 10)}px`, minHeight: '10px' }}
                      ></div>
                      <small className="text-muted mt-1">
                        {new Date(data.date).toLocaleDateString('en', { day: '2-digit', month: 'short' })}
                      </small>
                      <small className="fw-bold">{value}</small>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Top Workflows */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">{t('dashboard.topWorkflows')}</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>{t('dashboard.workflow')}</th>
                      <th>{t('dashboard.executions')}</th>
                      <th>{t('dashboard.successRate')}</th>
                      <th>{t('dashboard.avgTime')}</th>
                      <th>{t('dashboard.performance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topWorkflows.map((workflow, index) => (
                      <tr key={index}>
                        <td>
                          <div className="d-flex align-items-center">
                            <span className="badge bg-primary me-2">#{index + 1}</span>
                            {workflow.name}
                          </div>
                        </td>
                        <td>{workflow.executions.toLocaleString()}</td>
                        <td>
                          <span className={`badge ${workflow.successRate >= 95 ? 'bg-success' : workflow.successRate >= 90 ? 'bg-warning' : 'bg-danger'}`}>
                            {workflow.successRate}%
                          </span>
                        </td>
                        <td>{workflow.avgTime}s</td>
                        <td>
                          <div className="progress" style={{ height: '6px' }}>
                            <div 
                              className={`progress-bar ${workflow.successRate >= 95 ? 'bg-success' : workflow.successRate >= 90 ? 'bg-warning' : 'bg-danger'}`}
                              style={{ width: `${workflow.successRate}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="col-lg-4">
          {/* Error Breakdown */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="card-title mb-0">{t('dashboard.errorBreakdown')}</h6>
            </div>
            <div className="card-body">
              {errorBreakdown.map((error, index) => (
                <div key={index} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="small fw-medium">{error.type}</span>
                    <span className="small text-muted">{error.count} ({error.percentage}%)</span>
                  </div>
                  <div className="progress" style={{ height: '6px' }}>
                    <div 
                      className="progress-bar bg-danger"
                      style={{ width: `${error.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="card-title mb-0">{t('dashboard.systemPerformance')}</h6>
            </div>
            <div className="card-body">
              {performanceMetrics.map((metric, index) => (
                <div key={index} className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small fw-medium">{metric.metric}</span>
                    <div className="d-flex align-items-center gap-1">
                      <span className="small">{metric.current}%</span>
                      <span className={`small ${metric.trend === 'up' ? 'text-danger' : 'text-success'}`}>
                        {metric.trend === 'up' ? '↗' : '↘'}
                      </span>
                    </div>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div 
                      className={`progress-bar bg-${metric.color}`}
                      style={{ width: `${metric.current}%` }}
                    ></div>
                  </div>
                  <small className="text-muted">
                    {t('dashboard.previous')}: {metric.previous}%
                    <span className={`ms-1 ${metric.trend === 'up' ? 'text-danger' : 'text-success'}`}>
                      ({metric.trend === 'up' ? '+' : ''}{metric.current - metric.previous}%)
                    </span>
                  </small>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h6 className="card-title mb-0">{t('dashboard.quickActions')}</h6>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                <button className="btn btn-outline-primary btn-sm">
                  📊 {t('dashboard.generateReport')}
                </button>
                <button className="btn btn-outline-secondary btn-sm">
                  🔔 {t('dashboard.setAlertThresholds')}
                </button>
                <button className="btn btn-outline-info btn-sm">
                  📈 {t('dashboard.viewDetailedMetrics')}
                </button>
                <button className="btn btn-outline-warning btn-sm">
                  ⚠️ {t('dashboard.checkSystemHealth')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}